import { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/feature/Layout';

interface LiveEvent {
  id: number;
  type: string;
  label: string;
  campaign: string;
  source: string;
  device: string;
  city: string;
  time: string;
  color: string;
  icon: string;
}

const eventTemplates = [
  { type: 'page_view', label: 'Sayfa Görüntüleme', color: '#64B5F6', icon: 'ri-eye-line' },
  { type: 'campaign_view', label: 'Kampanya Görüntüleme', color: '#D4AF37', icon: 'ri-megaphone-line' },
  { type: 'whatsapp_click', label: 'WhatsApp Tıklaması', color: '#25D366', icon: 'ri-whatsapp-line' },
  { type: 'form_start', label: 'Form Başlatma', color: '#FFA726', icon: 'ri-file-edit-line' },
  { type: 'form_submit', label: 'Form Gönderimi', color: '#7CB342', icon: 'ri-checkbox-circle-line' },
  { type: 'button_click', label: 'Buton Tıklaması', color: '#AB47BC', icon: 'ri-cursor-line' },
  { type: 'video_play', label: 'Video Oynatma', color: '#FF7043', icon: 'ri-play-circle-line' },
  { type: 'lead_created', label: 'Lead Oluşturuldu', color: '#E57373', icon: 'ri-user-add-line' },
];

const campaigns = ['Botoks & Dolgu', 'Lazer Epilasyon', 'İpek Kirpik', 'Dermapen', 'SPA Günü', 'Hydra Facial'];
const sources = ['google', 'instagram', 'facebook', 'organic', 'tiktok'];
const devices = ['mobile', 'desktop', 'tablet'];
const cities = ['İstanbul', 'Ankara', 'İzmir', 'Bursa', 'Antalya', 'Adana'];

let eventCounter = 100;

function generateEvent(): LiveEvent {
  const tpl = eventTemplates[Math.floor(Math.random() * eventTemplates.length)];
  const now = new Date();
  eventCounter++;
  return {
    id: eventCounter,
    type: tpl.type,
    label: tpl.label,
    campaign: campaigns[Math.floor(Math.random() * campaigns.length)],
    source: sources[Math.floor(Math.random() * sources.length)],
    device: devices[Math.floor(Math.random() * devices.length)],
    city: cities[Math.floor(Math.random() * cities.length)],
    time: `${now.getHours().toString().padStart(2,'0')}:${now.getMinutes().toString().padStart(2,'0')}:${now.getSeconds().toString().padStart(2,'0')}`,
    color: tpl.color,
    icon: tpl.icon,
  };
}

const initialEvents: LiveEvent[] = Array.from({ length: 12 }, () => generateEvent()).reverse();

const liveStats = {
  activeNow: 47,
  todayVisitors: 1284,
  liveConversions: 3,
  formStarted: 8,
};

export default function RealtimeActivity() {
  const [events, setEvents] = useState<LiveEvent[]>(initialEvents);
  const [isLive, setIsLive] = useState(true);
  const [activeNow, setActiveNow] = useState(liveStats.activeNow);
  const [liveConversions, setLiveConversions] = useState(liveStats.liveConversions);
  const [newEventIds, setNewEventIds] = useState<Set<number>>(new Set());

  const addEvent = useCallback(() => {
    const newEv = generateEvent();
    setNewEventIds(prev => new Set([...prev, newEv.id]));
    setEvents(prev => [newEv, ...prev.slice(0, 49)]);
    setActiveNow(prev => prev + (Math.random() > 0.5 ? 1 : -1));
    if (newEv.type === 'lead_created') setLiveConversions(prev => prev + 1);
    setTimeout(() => setNewEventIds(prev => { const n = new Set(prev); n.delete(newEv.id); return n; }), 800);
  }, []);

  useEffect(() => {
    if (!isLive) return;
    const interval = setInterval(addEvent, 2200 + Math.random() * 1500);
    return () => clearInterval(interval);
  }, [isLive, addEvent]);

  const typeCounts = eventTemplates.map(tpl => ({
    ...tpl,
    count: events.filter(e => e.type === tpl.type).length,
  })).sort((a, b) => b.count - a.count).slice(0, 5);

  return (
    <Layout>
      <div className="space-y-5">
        {/* Live Status Bar */}
        <div className="bg-white rounded-2xl shadow-card p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${isLive ? 'bg-green-500' : 'bg-charcoal-300'}`} style={isLive ? { boxShadow: '0 0 0 4px rgba(34,197,94,0.2)' } : {}} />
              <span className="text-[14px] font-semibold text-charcoal-600">{isLive ? 'CANLI — Anlık veriler aktarılıyor' : 'DURDURULDU'}</span>
            </div>
            <button
              onClick={() => setIsLive(!isLive)}
              className={`flex items-center gap-2 h-[36px] px-4 rounded-xl text-[13px] font-medium transition-colors cursor-pointer whitespace-nowrap ${isLive ? 'bg-red-50 text-red-500 hover:bg-red-100' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${isLive ? 'ri-pause-circle-line' : 'ri-play-circle-line'} text-[15px]`} />
              </div>
              {isLive ? 'Duraklat' : 'Devam Et'}
            </button>
          </div>
        </div>

        {/* Live KPI Cards */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Şu An Aktif', value: Math.max(0, activeNow), icon: 'ri-user-location-line', color: '#25D366', pulse: true },
            { label: 'Bugünkü Ziyaretçi', value: liveStats.todayVisitors, icon: 'ri-group-line', color: '#D4AF37', pulse: false },
            { label: 'Canlı Dönüşüm', value: liveConversions, icon: 'ri-fire-line', color: '#E57373', pulse: true },
            { label: 'Form Başlatma', value: liveStats.formStarted, icon: 'ri-file-edit-line', color: '#64B5F6', pulse: false },
          ].map(kpi => (
            <div key={kpi.label} className="bg-white rounded-2xl shadow-card p-5 relative overflow-hidden">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[12px] text-charcoal-300">{kpi.label}</p>
                {kpi.pulse && (
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: kpi.color }} />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ backgroundColor: kpi.color }} />
                  </span>
                )}
              </div>
              <p className="text-[32px] font-bold text-charcoal-600 leading-tight">{kpi.value}</p>
              <div className="w-10 h-10 flex items-center justify-center rounded-xl absolute bottom-4 right-4 opacity-20" style={{ backgroundColor: kpi.color }}>
                <i className={`${kpi.icon} text-[18px]`} style={{ color: kpi.color }} />
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Live Event Stream */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-card overflow-hidden">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-[15px] font-semibold text-charcoal-600">Canlı Event Akışı</h3>
                <p className="text-[12px] text-charcoal-300 mt-0.5">Son {events.length} event</p>
              </div>
              <div className="flex items-center gap-1.5 text-[12px] text-green-600">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block" />
                Gerçek Zamanlı
              </div>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: 480 }}>
              {events.map(event => (
                <div
                  key={event.id}
                  className="flex items-center gap-4 px-5 py-3 border-b border-border transition-all duration-500"
                  style={newEventIds.has(event.id) ? { backgroundColor: `${event.color}08` } : {}}
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: `${event.color}18` }}>
                    <i className={`${event.icon} text-[13px]`} style={{ color: event.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[13px] font-semibold text-charcoal-600">{event.label}</span>
                      <span className="text-[11px] text-charcoal-300">•</span>
                      <span className="text-[12px] text-charcoal-400 truncate">{event.campaign}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-[11px] text-charcoal-300">{event.source}</span>
                      <span className="text-[11px] text-charcoal-300">•</span>
                      <span className="text-[11px] text-charcoal-300 capitalize">{event.device}</span>
                      <span className="text-[11px] text-charcoal-300">•</span>
                      <span className="text-[11px] text-charcoal-300">{event.city}</span>
                    </div>
                  </div>
                  <span className="text-[11px] text-charcoal-300 font-mono flex-shrink-0">{event.time}</span>
                  {newEventIds.has(event.id) && (
                    <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold text-white animate-bounce" style={{ backgroundColor: event.color }}>YENİ</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Event Type Breakdown */}
          <div className="bg-white rounded-2xl shadow-card p-6">
            <div className="mb-5">
              <h3 className="text-[15px] font-semibold text-charcoal-600">Event Dağılımı</h3>
              <p className="text-[12px] text-charcoal-300 mt-0.5">Son 50 event</p>
            </div>
            <div className="space-y-4">
              {typeCounts.map(t => (
                <div key={t.type}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${t.color}15` }}>
                        <i className={`${t.icon} text-[11px]`} style={{ color: t.color }} />
                      </div>
                      <span className="text-[12px] text-charcoal-500">{t.label}</span>
                    </div>
                    <span className="text-[12px] font-bold text-charcoal-600">{t.count}</span>
                  </div>
                  <div className="h-1.5 bg-charcoal-100 rounded-full">
                    <div className="h-1.5 rounded-full transition-all duration-500" style={{ width: `${(t.count / events.length) * 100}%`, backgroundColor: t.color }} />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-5 border-t border-border">
              <p className="text-[12px] font-semibold text-charcoal-400 mb-3">Aktif Kampanyalar</p>
              <div className="space-y-2">
                {campaigns.slice(0, 4).map(camp => {
                  const count = events.filter(e => e.campaign === camp).length;
                  return (
                    <div key={camp} className="flex items-center justify-between">
                      <span className="text-[12px] text-charcoal-500 truncate">{camp}</span>
                      <span className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block" />
                        <span className="text-[12px] font-semibold text-charcoal-600">{count}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
